import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowRight, ArrowLeft, Phone, Users, TrendingUp, DollarSign, 
  Clock, Building2, Download, Mail, Send, Loader2, CheckCircle2,
  Rocket, Zap, FileText, Target, Calendar, Sparkles, Check, Save, BarChart3,
  Minimize2, Maximize2, LineChart, Folder, MapPin
} from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAnimatedValue } from "@/hooks/useAnimatedValue";
import { generateROIPdf } from "@/utils/generateROIPdf";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { notifySlack } from "@/lib/slackNotify";

import { useSavedScenarios } from "@/hooks/useSavedScenarios";
import { useSwipeGesture } from "@/hooks/useSwipeGesture";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine
} from "recharts";

import StepProgress from "./roi-wizard/StepProgress";
import WizardSlider from "./roi-wizard/WizardSlider";
import SharePanel from "./roi-wizard/SharePanel";
import InfoBadge from "./roi-wizard/InfoBadge";
import CelebrationBanner from "./roi-wizard/CelebrationBanner";
import Confetti, { triggerConfetti } from "./roi-wizard/Confetti";
import QuickCalculateStep from "./roi-wizard/QuickCalculateStep";
import BenchmarkComparison from "./roi-wizard/BenchmarkComparison";
import SaveScenarioDialog from "./roi-wizard/SaveScenarioDialog";
import SavedScenariosPanel from "./roi-wizard/SavedScenariosPanel";
import ScenarioComparisonView from "./roi-wizard/ScenarioComparisonView";
import CollapsibleSection from "./roi-wizard/CollapsibleSection";
import CompactResultsSummary from "./roi-wizard/CompactResultsSummary";
import FloatingSummaryBar from "./roi-wizard/FloatingSummaryBar";

interface ROIWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Animation variants
const stepVariants = {
  initial: { opacity: 0, x: 50 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -50 }
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } }
};

const ROIWizard = ({ open, onOpenChange }: ROIWizardProps) => {
  const { language } = useLanguage();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(0);
  const [hasShownConfetti, setHasShownConfetti] = useState(false);
  const [quickMode, setQuickMode] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [isCompactView, setIsCompactView] = useState(false);
  const [showFloatingBar, setShowFloatingBar] = useState(false);
  const [countdown, setCountdown] = useState(120);
  const [countdownActive, setCountdownActive] = useState(false);
  const countdownIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  // Saved scenarios functionality
  const {
    scenarios,
    compareMode,
    setCompareMode,
    selectedForCompare,
    saveScenario,
    deleteScenario,
    toggleScenarioForCompare,
    loadScenario
  } = useSavedScenarios();
  
  // Step 1: Business Basics
  const [callsPerMonth, setCallsPerMonth] = useState(640);
  const [numberOfProperties, setNumberOfProperties] = useState(5);
  
  // Step 2: Revenue Impact
  const [missedCallPercent, setMissedCallPercent] = useState(40);
  const [salesCallPercent, setSalesCallPercent] = useState(20);
  const [avgOrderValue, setAvgOrderValue] = useState(300);
  
  // Step 3: Cost Analysis
  const [staffHoursWeek, setStaffHoursWeek] = useState(25);
  const [hourlyStaffCost, setHourlyStaffCost] = useState(25);

  // Step 5: Actions
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [honeypot, setHoneypot] = useState(''); // Honeypot field for bot detection
  const [isSubmittingEmail, setIsSubmittingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const formOpenedAtRef = useRef<number>(Date.now()); // Track when form opened for time-based bot detection

  // Fixed assumptions
  const LEAD_RATE = 0.70;
  const NO_CALLBACK_RATE = 1.00;
  const CONVERSION_RATE = 0.30;
  const SATISFACTION_DROP = 0.35;
  const PROMOTER_RATE = 0.15;
  const REFERRALS_PER_PROMOTER = 2.5;
  const REFERRAL_CONVERSION = 0.40;

  // Calculations
  const revenueLostCalc = useMemo(() => {
    const missedPct = quickMode ? 40 : missedCallPercent;
    const salesPct = quickMode ? 20 : salesCallPercent;
    const salesCalls = callsPerMonth * (salesPct / 100);
    const missedSalesCalls = salesCalls * (missedPct / 100);
    const potentialLeads = missedSalesCalls * LEAD_RATE;
    const lostLeads = potentialLeads * NO_CALLBACK_RATE;
    const lostConversions = lostLeads * CONVERSION_RATE;
    const monthlyLost = lostConversions * avgOrderValue;
    const yearlyLost = monthlyLost * 12;
    
    return {
      salesCalls: Math.round(salesCalls),
      missedSalesCalls: Math.round(missedSalesCalls),
      monthlyLost: Math.round(monthlyLost),
      yearlyLost: Math.round(yearlyLost)
    };
  }, [callsPerMonth, missedCallPercent, salesCallPercent, avgOrderValue, quickMode]);

  const promoterImpactCalc = useMemo(() => {
    const missedPct = quickMode ? 40 : missedCallPercent;
    const salesPct = quickMode ? 20 : salesCallPercent;
    const totalGuests = callsPerMonth * (salesPct / 100) * CONVERSION_RATE;
    const guestsWithMissedCalls = totalGuests * (missedPct / 100);
    const lostPromoters = guestsWithMissedCalls * SATISFACTION_DROP * PROMOTER_RATE;
    const lostReferrals = lostPromoters * REFERRALS_PER_PROMOTER * REFERRAL_CONVERSION;
    const monthlyValue = lostReferrals * avgOrderValue;
    const yearlyValue = monthlyValue * 12;

    return {
      lostPromoters: Math.round(lostPromoters * 10) / 10,
      lostReferrals: Math.round(lostReferrals * 10) / 10,
      monthlyValue: Math.round(monthlyValue),
      yearlyValue: Math.round(yearlyValue)
    };
  }, [callsPerMonth, missedCallPercent, salesCallPercent, avgOrderValue, quickMode]);

  const costsSavedCalc = useMemo(() => {
    const hoursWeek = quickMode ? 25 : staffHoursWeek;
    const hourlyCost = quickMode ? 25 : hourlyStaffCost;
    const weeklyStaffCost = hoursWeek * hourlyCost;
    const monthlyStaffCost = weeklyStaffCost * 4.33; // Total company-wide monthly cost
    const hostcallMonthlyCost = numberOfProperties * 149;
    const monthlySavings = Math.max(0, monthlyStaffCost - hostcallMonthlyCost);
    const yearlySavings = monthlySavings * 12;
    
    return {
      currentCost: Math.round(monthlyStaffCost),
      hostcallCost: hostcallMonthlyCost,
      monthlySavings: Math.round(monthlySavings),
      yearlySavings: Math.round(yearlySavings)
    };
  }, [staffHoursWeek, hourlyStaffCost, numberOfProperties, quickMode]);

  const totalImpactCalc = useMemo(() => ({
    revenueLost: revenueLostCalc.yearlyLost,
    promoterImpact: promoterImpactCalc.yearlyValue,
    costSavings: costsSavedCalc.yearlySavings,
    monthlyTotal: revenueLostCalc.monthlyLost + promoterImpactCalc.monthlyValue + costsSavedCalc.monthlySavings,
    yearlyTotal: revenueLostCalc.yearlyLost + promoterImpactCalc.yearlyValue + costsSavedCalc.yearlySavings
  }), [revenueLostCalc, promoterImpactCalc, costsSavedCalc]);

  const paybackCalc = useMemo(() => {
    const monthlyBenefit = totalImpactCalc.monthlyTotal;
    const hostcallMonthlyCost = costsSavedCalc.hostcallCost;
    
    if (monthlyBenefit <= 0) {
      return { months: Infinity, netMonthlyBenefit: 0, yearOneROI: 0, breakEvenDate: null };
    }
    
    const paybackMonths = hostcallMonthlyCost / monthlyBenefit;
    const today = new Date();
    const breakEvenDate = new Date(today);
    breakEvenDate.setMonth(breakEvenDate.getMonth() + Math.ceil(paybackMonths));
    
    const yearOneInvestment = hostcallMonthlyCost * 12;
    const yearOneBenefit = monthlyBenefit * 12;
    const yearOneROI = yearOneInvestment > 0 ? ((yearOneBenefit - yearOneInvestment) / yearOneInvestment) * 100 : 0;
    
    return { months: paybackMonths, netMonthlyBenefit: monthlyBenefit, yearOneROI, breakEvenDate };
  }, [totalImpactCalc, costsSavedCalc]);

  const chartData = useMemo(() => {
    const monthNames = language === 'sv' 
      ? ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec']
      : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    const now = new Date();
    const currentMonthIndex = now.getMonth();
    const currentYear = now.getFullYear();
    
    const months = [];
    let cumulativeLoss = 0;
    let cumulativeSavings = 0;
    const monthlyLoss = revenueLostCalc.monthlyLost + promoterImpactCalc.monthlyValue;
    const monthlyBenefit = totalImpactCalc.monthlyTotal;
    
    for (let i = 0; i < 12; i++) {
      cumulativeLoss -= monthlyLoss;
      cumulativeSavings += monthlyBenefit;
      
      const monthIndex = (currentMonthIndex + i) % 12;
      const year = currentYear + Math.floor((currentMonthIndex + i) / 12);
      const yearSuffix = `'${String(year).slice(-2)}`;
      
      months.push({
        month: `${monthNames[monthIndex]} ${yearSuffix}`,
        withoutHostcall: Math.round(cumulativeLoss),
        withHostcall: Math.round(cumulativeSavings),
        difference: Math.round(cumulativeSavings - cumulativeLoss)
      });
    }
    return months;
  }, [revenueLostCalc, promoterImpactCalc, totalImpactCalc, language]);

  // Calculate break-even month label for chart reference line
  const breakEvenMonthLabel = useMemo(() => {
    if (paybackCalc.months <= 0 || paybackCalc.months > 12 || !isFinite(paybackCalc.months)) {
      return null;
    }
    const breakEvenIndex = Math.ceil(paybackCalc.months) - 1;
    if (breakEvenIndex >= 0 && breakEvenIndex < chartData.length) {
      return chartData[breakEvenIndex]?.month;
    }
    return null;
  }, [paybackCalc.months, chartData]);

  // Validate if staff hours seem too low relative to property count
  const staffHoursWarning = useMemo(() => {
    const hoursPerProperty = staffHoursWeek / numberOfProperties;
    if (hoursPerProperty < 0.5 && numberOfProperties > 10) {
      return language === 'sv' 
        ? `⚠️ Endast ${hoursPerProperty.toFixed(1)} tim/fastighet – verkar lågt`
        : `⚠️ Only ${hoursPerProperty.toFixed(1)} hrs/property – seems low`;
    }
    if (hoursPerProperty < 1 && numberOfProperties > 20) {
      return language === 'sv'
        ? `💡 ${hoursPerProperty.toFixed(1)} tim/fastighet – är detta totala timmar?`
        : `💡 ${hoursPerProperty.toFixed(1)} hrs/property – is this total hours?`;
    }
    return undefined;
  }, [staffHoursWeek, numberOfProperties, language]);

  // Validate if hourly staff cost seems appropriate for region
  const hourlyCostWarning = useMemo(() => {
    // US/Canada typical range: $18-45/hr, Europe: $20-50/hr
    if (hourlyStaffCost < 18) {
      return language === 'sv' 
        ? `⚠️ $${hourlyStaffCost}/tim är lågt – inkludera förmåner och overheadkostnader`
        : `⚠️ $${hourlyStaffCost}/hr is low – include benefits & overhead costs`;
    }
    if (hourlyStaffCost > 55) {
      return language === 'sv'
        ? `💡 $${hourlyStaffCost}/tim är högt – är detta fullt belastad kostnad?`
        : `💡 $${hourlyStaffCost}/hr is high – is this fully-loaded cost?`;
    }
    return undefined;
  }, [hourlyStaffCost, language]);

  // Validate if missed call percentage seems realistic based on industry benchmarks
  const missedCallWarning = useMemo(() => {
    // Industry benchmarks: 15-45% is typical for vacation rentals
    if (missedCallPercent < 15) {
      return language === 'sv' 
        ? `💡 ${missedCallPercent}% är under branschsnittet (15-45%) – räknar du alla samtalstyper?`
        : `💡 ${missedCallPercent}% is below industry average (15-45%) – counting all call types?`;
    }
    if (missedCallPercent > 60) {
      return language === 'sv'
        ? `⚠️ ${missedCallPercent}% är högt – detta indikerar betydande intäktsförlust`
        : `⚠️ ${missedCallPercent}% is high – this indicates significant revenue leakage`;
    }
    return undefined;
  }, [missedCallPercent, language]);

  // Validate average order value based on property count (indicator of property type)
  const avgOrderWarning = useMemo(() => {
    // Luxury properties typically have higher values, budget properties lower
    const valuePerProperty = avgOrderValue;
    if (valuePerProperty < 100 && numberOfProperties < 10) {
      return language === 'sv' 
        ? `💡 $${avgOrderValue} är typiskt för budget/hostel – är detta din snittbokning?`
        : `💡 $${avgOrderValue} is typical for budget/hostel – is this your avg booking?`;
    }
    if (valuePerProperty > 600) {
      return language === 'sv'
        ? `✨ $${avgOrderValue} indikerar lyxboende – bra marginal för AI-investering!`
        : `✨ $${avgOrderValue} indicates luxury properties – great ROI potential!`;
    }
    if (valuePerProperty < 150 && numberOfProperties > 20) {
      return language === 'sv'
        ? `⚠️ Lågt värde för ${numberOfProperties} fastigheter – kontrollera att det är snitt per bokning`
        : `⚠️ Low value for ${numberOfProperties} properties – verify this is avg per booking`;
    }
    return undefined;
  }, [avgOrderValue, numberOfProperties, language]);

  // Regional hourly cost benchmarks for tooltip
  const regionalBenchmarks = useMemo(() => ({
    title: language === 'sv' ? 'Regionala riktmärken' : 'Regional Benchmarks',
    content: language === 'sv'
      ? '🇺🇸 USA: $22-45/tim\n🇪🇺 Europa: $18-40/tim\n🇬🇧 UK: $16-35/tim\n🌏 APAC: $8-25/tim\n\nInkludera lön + förmåner + overhead för fullt belastad kostnad.'
      : '🇺🇸 US: $22-45/hr\n🇪🇺 Europe: $18-40/hr\n🇬🇧 UK: $16-35/hr\n🌏 APAC: $8-25/hr\n\nInclude salary + benefits + overhead for fully-loaded cost.'
  }), [language]);

  // Detect user's region and suggest appropriate hourly cost
  const [detectedRegion, setDetectedRegion] = useState<string | null>(null);
  const detectRegion = useCallback(() => {
    try {
      const locale = navigator.language || 'en-US';
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      let region = 'US';
      let suggestedCost = 32;
      let regionLabel = language === 'sv' ? 'USA' : 'US';
      
      // Detect based on timezone and locale
      if (timezone.includes('Europe') || locale.startsWith('de') || locale.startsWith('fr') || locale.startsWith('es') || locale.startsWith('it') || locale.startsWith('nl')) {
        if (timezone.includes('London') || locale.startsWith('en-GB')) {
          region = 'UK';
          suggestedCost = 26;
          regionLabel = 'UK';
        } else if (locale.startsWith('sv') || timezone.includes('Stockholm')) {
          region = 'Nordic';
          suggestedCost = 35;
          regionLabel = language === 'sv' ? 'Norden' : 'Nordic';
        } else {
          region = 'Europe';
          suggestedCost = 28;
          regionLabel = language === 'sv' ? 'Europa' : 'Europe';
        }
      } else if (timezone.includes('Asia') || timezone.includes('Australia') || timezone.includes('Pacific')) {
        if (timezone.includes('Australia') || timezone.includes('Sydney') || timezone.includes('Melbourne')) {
          region = 'Australia';
          suggestedCost = 30;
          regionLabel = language === 'sv' ? 'Australien' : 'Australia';
        } else {
          region = 'APAC';
          suggestedCost = 18;
          regionLabel = 'APAC';
        }
      } else if (locale.startsWith('en-CA') || timezone.includes('Canada') || timezone.includes('Toronto') || timezone.includes('Vancouver')) {
        region = 'Canada';
        suggestedCost = 30;
        regionLabel = 'Canada';
      }
      
      setHourlyStaffCost(suggestedCost);
      setDetectedRegion(regionLabel);
      
      // Show toast notification
      toast({
        title: language === 'sv' ? `Region upptäckt: ${regionLabel}` : `Region detected: ${regionLabel}`,
        description: language === 'sv' 
          ? `Timkostnad justerad till $${suggestedCost}/tim baserat på ${regionLabel} riktmärken`
          : `Hourly cost adjusted to $${suggestedCost}/hr based on ${regionLabel} benchmarks`,
      });
    } catch (error) {
      console.error('Error detecting region:', error);
      toast({
        title: language === 'sv' ? 'Kunde inte upptäcka region' : 'Could not detect region',
        description: language === 'sv' ? 'Ange din timkostnad manuellt' : 'Please enter your hourly cost manually',
        variant: 'destructive',
      });
    }
  }, [language, setHourlyStaffCost, toast]);
  useEffect(() => {
    if (currentStep === 3 && paybackCalc.months < 3 && !hasShownConfetti && !quickMode) {
      setHasShownConfetti(true);
      triggerConfetti();
    }
  }, [currentStep, paybackCalc.months, hasShownConfetti, quickMode]);

  // Reset confetti state and countdown when wizard closes
  useEffect(() => {
    if (!open) {
      setHasShownConfetti(false);
      setShowFloatingBar(false);
      // Reset countdown when modal closes
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      setCountdown(120);
      setCountdownActive(false);
    } else {
      // Start countdown when modal opens
      setCountdown(120);
      setCountdownActive(true);
      // Reset form opened timestamp for time-based bot detection
      formOpenedAtRef.current = Date.now();
    }
  }, [open]);

  // Countdown timer logic
  useEffect(() => {
    if (countdownActive && countdown > 0) {
      countdownIntervalRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            setCountdownActive(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [countdownActive]);

  // Stop countdown when user reaches results
  useEffect(() => {
    if (currentStep >= 3 && countdownActive) {
      setCountdownActive(false);
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    }
  }, [currentStep, countdownActive]);

  // Format countdown display
  const formatCountdown = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle scroll to show/hide floating bar on results step
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (currentStep === 3) {
      const scrollTop = e.currentTarget.scrollTop;
      setShowFloatingBar(scrollTop > 100);
    }
  }, [currentStep]);

  // Scroll to top handler
  const scrollToTop = useCallback(() => {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Swipe gesture for step navigation
  const { handlers: swipeHandlers } = useSwipeGesture({
    onSwipeLeft: () => {
      if (!quickMode && currentStep < steps.length - 1) {
        setCurrentStep(prev => prev + 1);
      }
    },
    onSwipeRight: () => {
      if (!quickMode && currentStep > 0) {
        setCurrentStep(prev => prev - 1);
      }
    },
    threshold: 80
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat(language === 'sv' ? 'sv-SE' : 'en-US', {
      style: 'currency',
      currency: language === 'sv' ? 'SEK' : 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const handleDownloadPdf = () => {
    generateROIPdf({
      language: language as 'en' | 'sv',
      calculationType: 'full-report',
      inputs: { callsPerMonth, missedCallPercent, salesCallPercent, avgOrderValue, staffHoursWeek, hourlyStaffCost, numberOfProperties },
      results: { monthlyValue: revenueLostCalc.monthlyLost, yearlyValue: revenueLostCalc.yearlyLost },
      promoterImpact: { monthlyValue: promoterImpactCalc.monthlyValue, yearlyValue: promoterImpactCalc.yearlyValue, lostPromoters: promoterImpactCalc.lostPromoters },
      costSavings: { monthlyValue: costsSavedCalc.monthlySavings, yearlyValue: costsSavedCalc.yearlySavings },
      totalImpact: { monthlyTotal: totalImpactCalc.monthlyTotal, yearlyTotal: totalImpactCalc.yearlyTotal },
      financialSummary: { paybackMonths: paybackCalc.months, yearOneROI: paybackCalc.yearOneROI },
      generatedAt: new Date()
    });
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmittingEmail(true);
    
    // Calculate time spent on form for bot detection
    const formDurationMs = Date.now() - formOpenedAtRef.current;
    
    try {
      const { error } = await supabase.functions.invoke('send-roi-report', {
        body: {
          email, name, language,
          website: honeypot, // Honeypot field - should be empty for real users
          formDurationMs, // Time-based bot detection
          calculationType: 'full-report',
          inputs: { callsPerMonth, missedCallPercent, salesCallPercent, avgOrderValue, staffHoursWeek, hourlyStaffCost, numberOfProperties },
          results: { monthlyValue: revenueLostCalc.monthlyLost, yearlyValue: revenueLostCalc.yearlyLost },
          promoterImpact: { monthlyValue: promoterImpactCalc.monthlyValue, yearlyValue: promoterImpactCalc.yearlyValue, lostPromoters: promoterImpactCalc.lostPromoters },
          costSavings: { monthlyValue: costsSavedCalc.monthlySavings, yearlyValue: costsSavedCalc.yearlySavings },
          totalImpact: { monthlyTotal: totalImpactCalc.monthlyTotal, yearlyTotal: totalImpactCalc.yearlyTotal },
          financialSummary: { paybackMonths: paybackCalc.months, yearOneROI: paybackCalc.yearOneROI }
        }
      });
      
      if (error) throw error;
      notifySlack({
        type: "roi",
        title: `ROI report requested by ${name || email}`,
        fields: {
          Email: email,
          Properties: numberOfProperties,
          "Calls/month": callsPerMonth,
          "Yearly loss": totalImpactCalc.yearlyTotal,
          "Payback (months)": Number.isFinite(paybackCalc.months) ? paybackCalc.months.toFixed(1) : "n/a",
        },
      });
      setEmailSent(true);
      toast({ title: language === 'sv' ? 'Rapport skickad!' : 'Report sent!', description: language === 'sv' ? 'Kolla din inbox' : 'Check your inbox' });

    } catch (error) {
      console.error('Error sending report:', error);
      toast({ title: language === 'sv' ? 'Fel' : 'Error', description: language === 'sv' ? 'Kunde inte skicka rapporten' : 'Could not send report', variant: 'destructive' });
    } finally {
      setIsSubmittingEmail(false);
    }
  };

  // Handle loading a saved scenario
  const handleLoadScenario = (scenario: ReturnType<typeof loadScenario>) => {
    if (!scenario) return;
    setCallsPerMonth(scenario.inputs.callsPerMonth);
    setNumberOfProperties(scenario.inputs.numberOfProperties);
    setMissedCallPercent(scenario.inputs.missedCallPercent);
    setSalesCallPercent(scenario.inputs.salesCallPercent);
    setAvgOrderValue(scenario.inputs.avgOrderValue);
    setStaffHoursWeek(scenario.inputs.staffHoursWeek);
    setHourlyStaffCost(scenario.inputs.hourlyStaffCost);
    setQuickMode(false);
    setCurrentStep(3); // Go to results
    toast({
      title: language === 'sv' ? 'Scenario laddat' : 'Scenario loaded',
      description: scenario.name
    });
  };

  // Get current inputs and results for saving
  const getCurrentInputs = () => ({
    callsPerMonth,
    numberOfProperties,
    missedCallPercent,
    salesCallPercent,
    avgOrderValue,
    staffHoursWeek,
    hourlyStaffCost
  });

  const getCurrentResults = () => ({
    revenueLost: totalImpactCalc.revenueLost,
    promoterImpact: totalImpactCalc.promoterImpact,
    costSavings: totalImpactCalc.costSavings,
    totalYearly: totalImpactCalc.yearlyTotal,
    paybackMonths: paybackCalc.months,
    yearOneROI: paybackCalc.yearOneROI
  });

  const steps = [
    { id: 'basics', title: language === 'sv' ? 'Grundläggande' : 'Business Basics', subtitle: language === 'sv' ? 'Berätta om din verksamhet' : 'Tell us about your operation', label: language === 'sv' ? 'Start' : 'Start' },
    { id: 'revenue', title: language === 'sv' ? 'Intäktspåverkan' : 'Revenue Impact', subtitle: language === 'sv' ? 'Förstå kostnaden av missade samtal' : 'Understand missed call costs', label: language === 'sv' ? 'Intäkt' : 'Revenue' },
    { id: 'costs', title: language === 'sv' ? 'Kostnadsanalys' : 'Cost Analysis', subtitle: language === 'sv' ? 'Nuvarande personalkostnader' : 'Current staff expenses', label: language === 'sv' ? 'Kostnad' : 'Costs' },
    { id: 'results', title: language === 'sv' ? 'Dina resultat' : 'Your Results', subtitle: language === 'sv' ? 'Se dina potentiella besparingar' : 'See your potential savings', label: language === 'sv' ? 'Resultat' : 'Results' },
    { id: 'actions', title: language === 'sv' ? 'Vidta åtgärder' : 'Take Action', subtitle: language === 'sv' ? 'Börja spara idag' : 'Start saving today', label: language === 'sv' ? 'Åtgärd' : 'Action' }
  ];

  const tr = {
    next: language === 'sv' ? 'Nästa' : 'Next',
    back: language === 'sv' ? 'Tillbaka' : 'Back',
    done: language === 'sv' ? 'Klar' : 'Done',
    getReport: language === 'sv' ? 'Se hela rapporten' : 'Get Your Full Report',
    stepOf: language === 'sv' ? 'av' : 'of',
    callsPerMonth: language === 'sv' ? 'Samtal per månad' : 'Calls per Month',
    properties: language === 'sv' ? 'Antal fastigheter' : 'Number of Properties',
    missedPercent: language === 'sv' ? 'Missade samtal %' : 'Missed Call %',
    salesPercent: language === 'sv' ? 'Försäljningssamtal %' : 'Sales Calls %',
    avgOrder: language === 'sv' ? 'Snittvärde per vistelse' : 'Avg Stay Value',
    staffHours: language === 'sv' ? 'Personaltimmar/vecka för gästkommunikation' : 'Staff Hours/Week for Guest Communication',
    hourlyCost: language === 'sv' ? 'Timkostnad personal' : 'Hourly Staff Cost',
    howWeCalc: language === 'sv' ? 'Hur vi räknar' : 'How we calculate',
    promoterImpact: language === 'sv' ? 'Förlorade ambassadörer' : 'Missed Promoter Impact',
    totalAnnual: language === 'sv' ? 'Total årlig påverkan' : 'Total Annual Impact',
    revenueLost: language === 'sv' ? 'Förlorade intäkter' : 'Revenue Lost',
    promoterValue: language === 'sv' ? 'Ambassadörspåverkan' : 'Promoter Impact',
    costSavings: language === 'sv' ? 'Kostnadsbesparingar' : 'Cost Savings',
    paybackTitle: language === 'sv' ? 'Återbetalningstid' : 'Payback Period',
    months: language === 'sv' ? 'månader' : 'months',
    yearOneROI: language === 'sv' ? 'År 1 ROI' : 'Year 1 ROI',
    breakEven: language === 'sv' ? 'Break-even nås' : 'Break-even by',
    emailReport: language === 'sv' ? 'Skicka rapport till e-post' : 'Email Your Report',
    downloadPdf: language === 'sv' ? 'Ladda ner PDF-rapport' : 'Download PDF Report',
    bookDemo: language === 'sv' ? 'Boka en demo' : 'Book a Demo',
    freeTrial: language === 'sv' ? 'Starta gratis provperiod' : 'Start Free Trial',
    fullAnalysis: language === 'sv' ? 'Fullständig analys' : 'Full Analysis',
    quickCalc: language === 'sv' ? 'Snabbkalkyl' : 'Quick Calculate'
  };

  const animatedYearlyTotal = useAnimatedValue({ value: totalImpactCalc.yearlyTotal });

  // Custom chart tooltip
  const CustomChartTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload) return null;
    return (
      <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
        <p className="font-semibold text-sm mb-2">{label}</p>
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex items-center gap-2 text-sm">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
            <span className="text-muted-foreground">
              {entry.dataKey === 'withoutHostcall' ? (language === 'sv' ? 'Utan' : 'Without') : (language === 'sv' ? 'Med' : 'With')}:
            </span>
            <span className={entry.value < 0 ? 'text-red-500' : 'text-green-500'}>{formatCurrency(entry.value)}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0: // Business Basics
        return (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-6"
          >
            <motion.div variants={itemVariants} className="text-center mb-4">
              <h3 className="text-2xl sm:text-3xl font-bold mb-2">{language === 'sv' ? 'Låt oss börja!' : "Let's get started!"}</h3>
              <p className="text-base text-muted-foreground">{language === 'sv' ? 'Berätta om din samtalsvolym' : 'Tell us about your call volume'}</p>
            </motion.div>
            <motion.div variants={itemVariants}>
              <WizardSlider
                label={language === 'sv' ? 'Totala samtal per månad' : 'Total Calls per Month'}
                helperText={language === 'sv' ? 'Alla fastigheter kombinerat' : 'All properties combined'}
                value={callsPerMonth}
                onChange={setCallsPerMonth}
                min={50}
                max={2000}
                step={10}
                icon={Phone}
                animated
              />
            </motion.div>
            <motion.div variants={itemVariants}>
              <WizardSlider
                label={tr.properties}
                value={numberOfProperties}
                onChange={setNumberOfProperties}
                min={1}
                max={500}
                step={numberOfProperties <= 50 ? 1 : numberOfProperties <= 100 ? 5 : 10}
                icon={Building2}
                animated
              />
            </motion.div>
          </motion.div>
        );
      
      case 1: // Revenue Impact
        return (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-6"
          >
            <motion.div variants={itemVariants} className="text-center mb-4">
              <h3 className="text-2xl sm:text-3xl font-bold mb-2">{language === 'sv' ? 'Intäktspåverkan' : 'Revenue Impact'}</h3>
              <p className="text-base text-muted-foreground">{language === 'sv' ? 'Förstå kostnaden av missade samtal' : 'Understand the cost of missed calls'}</p>
            </motion.div>
            <motion.div variants={itemVariants}>
              <WizardSlider
                label={tr.missedPercent}
                value={missedCallPercent}
                onChange={setMissedCallPercent}
                min={5}
                max={80}
                suffix="%"
                icon={TrendingUp}
                animated
                helperText={missedCallWarning}
                infoBadge={{
                  title: language === 'sv' ? 'Branschriktmärke' : 'Industry Benchmark',
                  content: language === 'sv' 
                    ? 'Semesteruthyrning: 25-45% missade\nHotell: 15-30% missade\nBoutique: 20-40% missade\n\nInkluderar efter-timmar, hög säsong & samtidiga samtal.'
                    : 'Vacation rentals: 25-45% missed\nHotels: 15-30% missed\nBoutique properties: 20-40% missed\n\nIncludes after-hours, peak season & concurrent calls.',
                  variant: 'green'
                }}
              />
            </motion.div>
            <motion.div variants={itemVariants}>
              <WizardSlider
                label={tr.salesPercent}
                value={salesCallPercent}
                onChange={setSalesCallPercent}
                min={5}
                max={50}
                suffix="%"
                icon={Users}
                animated
              />
            </motion.div>
            <motion.div variants={itemVariants}>
              <WizardSlider
                label={tr.avgOrder}
                value={avgOrderValue}
                onChange={setAvgOrderValue}
                min={50}
                max={1000}
                step={10}
                prefix="$"
                icon={DollarSign}
                animated
                helperText={avgOrderWarning}
                infoBadge={{
                  title: language === 'sv' ? 'Fastighetstyp' : 'Property Type',
                  content: language === 'sv' 
                    ? '💰 Budget/Hostel: $50-150\n🏠 Standard: $150-350\n🌟 Premium: $350-600\n✨ Lyxboende: $600+\n\nAnge genomsnittligt bokningsvärde, inte per natt.'
                    : '💰 Budget/Hostel: $50-150\n🏠 Standard: $150-350\n🌟 Premium: $350-600\n✨ Luxury: $600+\n\nEnter average booking value, not per night.',
                  variant: 'green'
                }}
              />
            </motion.div>
            
            {/* Promoter Impact Info */}
            <motion.div variants={itemVariants}>
              <Card className="p-5 bg-purple-50/50 dark:bg-purple-900/10 border-purple-200/50 dark:border-purple-800/30">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl shrink-0">
                    <Sparkles className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <h4 className="font-bold text-lg text-purple-800 dark:text-purple-200">{tr.promoterImpact}</h4>
                      <Badge className="bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300 text-sm">
                        +{formatCurrency(promoterImpactCalc.yearlyValue)}/yr
                      </Badge>
                      <InfoBadge 
                        title={tr.howWeCalc}
                        content={language === 'sv' 
                          ? 'Nöjdhetsminskning: 35% | Ambassadörsgrad: 15% | 2,5 hänvisningar/ambassadör | 40% konvertering'
                          : 'Satisfaction drop: 35% | Promoter rate: 15% | 2.5 referrals/promoter | 40% conversion'}
                        variant="purple"
                      />
                    </div>
                    <p className="text-base text-purple-700 dark:text-purple-300">
                      {language === 'sv' 
                        ? `Dålig support = missnöjda gäster = ingen mun-till-mun. Med ${promoterImpactCalc.lostPromoters} förlorade ambassadörer per månad.`
                        : `Poor support = dissatisfied guests = no word-of-mouth. With ${promoterImpactCalc.lostPromoters} lost promoters monthly.`}
                    </p>
                  </div>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        );
      
      case 2: // Cost Analysis
        return (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-6"
          >
            <motion.div variants={itemVariants} className="text-center mb-4">
              <h3 className="text-2xl sm:text-3xl font-bold mb-2">{language === 'sv' ? 'Kostnadsanalys' : 'Cost Analysis'}</h3>
              <p className="text-base text-muted-foreground">{language === 'sv' ? 'Nuvarande personalkostnader för samtalshantering' : 'Current staff costs for handling calls'}</p>
            </motion.div>
            <motion.div variants={itemVariants}>
              <WizardSlider
                label={tr.staffHours}
                value={staffHoursWeek}
                onChange={setStaffHoursWeek}
                min={5}
                max={80}
                icon={Clock}
                animated
                helperText={staffHoursWarning}
                infoBadge={{
                  title: language === 'sv' ? 'Totala timmar' : 'Total Hours',
                  content: language === 'sv' 
                    ? 'Ange totala timmar per vecka för gästkommunikation över alla fastigheter, inte per fastighet.'
                    : 'Enter total hours per week spent on guest communication across all properties, not per property.',
                  variant: 'purple'
                }}
              />
            </motion.div>
            <motion.div variants={itemVariants}>
              <WizardSlider
                label={tr.hourlyCost}
                value={hourlyStaffCost}
                onChange={setHourlyStaffCost}
                min={15}
                max={75}
                prefix="$"
                icon={DollarSign}
                animated
                helperText={hourlyCostWarning}
                infoBadge={{
                  title: regionalBenchmarks.title,
                  content: regionalBenchmarks.content,
                  variant: 'amber'
                }}
              />
              
              {/* Detect Region Button */}
              <div className="flex items-center gap-2 mt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={detectRegion}
                  className="text-xs gap-1.5"
                >
                  <MapPin className="h-3.5 w-3.5" />
                  {language === 'sv' ? 'Upptäck min region' : 'Detect my region'}
                </Button>
                {detectedRegion && (
                  <span className="text-xs text-muted-foreground">
                    {language === 'sv' ? `Upptäckt: ${detectedRegion}` : `Detected: ${detectedRegion}`}
                  </span>
                )}
              </div>
            </motion.div>
            
            {/* Cost comparison summary */}
            <motion.div variants={itemVariants}>
              <Card className="p-6 bg-green-50/50 dark:bg-green-900/10 border-green-200/50 dark:border-green-800/30">
                <div className="grid grid-cols-2 gap-6 text-center">
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">{language === 'sv' ? 'Nuvarande kostnad' : 'Current Cost'}</p>
                    <motion.p 
                      className="text-2xl sm:text-3xl font-bold text-red-500"
                      key={costsSavedCalc.currentCost}
                      initial={{ scale: 1.1 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring" }}
                    >
                      {formatCurrency(costsSavedCalc.currentCost)}/mo
                    </motion.p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">{language === 'sv' ? 'Med HostCall.ai' : 'With HostCall.ai'}</p>
                    <p className="text-2xl sm:text-3xl font-bold text-green-600">{formatCurrency(costsSavedCalc.hostcallCost)}/mo</p>
                  </div>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        );
      
      case 3: // Results
        return (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-6"
          >
            {paybackCalc.months < 3 && !isCompactView && (
              <motion.div variants={itemVariants}>
                <CelebrationBanner language={language} />
              </motion.div>
            )}

            {/* Compact View Summary - Always visible in compact mode */}
            {isCompactView && (
              <motion.div variants={itemVariants}>
                <CompactResultsSummary
                  annualImpact={totalImpactCalc.yearlyTotal}
                  paybackMonths={paybackCalc.months}
                  yearOneROI={paybackCalc.yearOneROI}
                  formatCurrency={formatCurrency}
                  language={language}
                />
              </motion.div>
            )}
            
            {/* Total Impact - Full view only */}
            {!isCompactView && (
              <motion.div variants={itemVariants}>
                <Card className="p-6 sm:p-8 bg-gradient-to-br from-brand-600 via-brand-500 to-pink-500 text-white shadow-2xl overflow-hidden relative">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16" />
                  <div className="relative z-10">
                    <div className="flex items-center gap-3 mb-4">
                      <Sparkles className="h-6 w-6" />
                      <h3 className="text-lg sm:text-xl font-bold">{tr.totalAnnual}</h3>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 sm:gap-6 mb-4">
                      <div>
                        <p className="text-xs sm:text-sm text-white/70 mb-1">{tr.revenueLost}</p>
                        <p className="text-sm sm:text-lg font-bold">{formatCurrency(totalImpactCalc.revenueLost)}</p>
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm text-white/70 mb-1">{tr.promoterValue}</p>
                        <p className="text-sm sm:text-lg font-bold">{formatCurrency(totalImpactCalc.promoterImpact)}</p>
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm text-white/70 mb-1">{tr.costSavings}</p>
                        <p className="text-sm sm:text-lg font-bold">{formatCurrency(totalImpactCalc.costSavings)}</p>
                      </div>
                    </div>
                    
                    <div className="border-t border-white/30 pt-4">
                      <motion.p 
                        className="text-3xl sm:text-4xl lg:text-5xl font-bold"
                        initial={{ scale: 0.5, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ type: "spring", delay: 0.3 }}
                      >
                        {formatCurrency(Math.round(animatedYearlyTotal))}
                      </motion.p>
                      <p className="text-sm text-white/70 mt-1">{language === 'sv' ? 'per år' : 'per year'}</p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            )}

            {/* Two-column layout on desktop */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Industry Benchmark Comparison */}
              {!isCompactView ? (
                <motion.div variants={itemVariants}>
                  <BenchmarkComparison 
                    userPayback={paybackCalc.months}
                    userROI={paybackCalc.yearOneROI}
                    propertyCount={numberOfProperties}
                    language={language}
                  />
                </motion.div>
              ) : (
                <motion.div variants={itemVariants}>
                  <CollapsibleSection
                    title={language === 'sv' ? 'Branschjämförelse' : 'Industry Benchmark'}
                    icon={BarChart3}
                    defaultExpanded={false}
                    badge={<Badge variant="outline" className="text-[10px] ml-2">{language === 'sv' ? 'Top 25%' : 'Top 25%'}</Badge>}
                  >
                    <BenchmarkComparison 
                      userPayback={paybackCalc.months}
                      userROI={paybackCalc.yearOneROI}
                      propertyCount={numberOfProperties}
                      language={language}
                    />
                  </CollapsibleSection>
                </motion.div>
              )}

              {/* Payback Period */}
              <motion.div variants={itemVariants}>
                <Card className="p-3 h-full">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="h-4 w-4 text-brand-500" />
                    <h4 className="font-semibold text-xs sm:text-sm">{tr.paybackTitle}</h4>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <motion.p 
                        className="text-xl sm:text-2xl font-bold text-brand-600"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", delay: 0.2 }}
                      >
                        {paybackCalc.months < 1 ? '<1' : paybackCalc.months.toFixed(1)}
                      </motion.p>
                      <p className="text-[9px] sm:text-[10px] text-muted-foreground">{tr.months}</p>
                    </div>
                    <div>
                      <motion.p 
                        className="text-xl sm:text-2xl font-bold text-green-600"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", delay: 0.3 }}
                      >
                        {paybackCalc.yearOneROI.toFixed(0)}%
                      </motion.p>
                      <p className="text-[9px] sm:text-[10px] text-muted-foreground">{tr.yearOneROI}</p>
                    </div>
                    <div>
                      <motion.p 
                        className="text-[10px] sm:text-xs font-semibold text-brand-600"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.4 }}
                      >
                        {paybackCalc.breakEvenDate?.toLocaleDateString(language === 'sv' ? 'sv-SE' : 'en-US', { month: 'short', year: 'numeric' })}
                      </motion.p>
                      <p className="text-[9px] sm:text-[10px] text-muted-foreground">{tr.breakEven}</p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            </div>

            {/* Chart - Collapsible in compact mode */}
            {!isCompactView ? (
              <motion.div variants={itemVariants}>
                <Card className="p-2 sm:p-3">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-[10px] sm:text-xs">{language === 'sv' ? '12-månaders påverkan' : '12-Month Impact'}</h4>
                    <div className="flex gap-2 text-[9px] sm:text-[10px]">
                      <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-red-400" />{language === 'sv' ? 'Utan' : 'Without'}</span>
                      <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-green-500" />{language === 'sv' ? 'Med' : 'With'}</span>
                    </div>
                  </div>
                  <div className="h-24 sm:h-28 lg:h-32">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                        <defs>
                          <linearGradient id="colorWithoutWizard" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#f87171" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#f87171" stopOpacity={0}/>
                          </linearGradient>
                          <linearGradient id="colorWithWizard" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="month" tick={{ fontSize: 8, fill: 'hsl(var(--muted-foreground))' }} />
                        <YAxis tickFormatter={(v) => `${v < 0 ? '-' : ''}$${Math.abs(v / 1000).toFixed(0)}k`} tick={{ fontSize: 8, fill: 'hsl(var(--muted-foreground))' }} />
                        <RechartsTooltip content={<CustomChartTooltip />} />
                        <ReferenceLine y={0} stroke="hsl(var(--border))" strokeWidth={1} />
                        {breakEvenMonthLabel && (
                          <ReferenceLine 
                            x={breakEvenMonthLabel} 
                            stroke="#F05B27" 
                            strokeWidth={2} 
                            strokeDasharray="4 4"
                            label={{ 
                              value: language === 'sv' ? 'Break-even' : 'Break-even', 
                              position: 'top', 
                              fontSize: 9, 
                              fill: '#F05B27',
                              fontWeight: 600
                            }} 
                          />
                        )}
                        <Area type="monotone" dataKey="withoutHostcall" stroke="#f87171" fill="url(#colorWithoutWizard)" strokeWidth={2} />
                        <Area type="monotone" dataKey="withHostcall" stroke="#22c55e" fill="url(#colorWithWizard)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </Card>
              </motion.div>
            ) : (
              <motion.div variants={itemVariants}>
                <CollapsibleSection
                  title={language === 'sv' ? '12-månaders diagram' : '12-Month Chart'}
                  icon={LineChart}
                  defaultExpanded={false}
                >
                  <div className="h-24">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                        <defs>
                          <linearGradient id="colorWithoutWizard" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#f87171" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#f87171" stopOpacity={0}/>
                          </linearGradient>
                          <linearGradient id="colorWithWizard" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="month" tick={{ fontSize: 8, fill: 'hsl(var(--muted-foreground))' }} />
                        <YAxis tickFormatter={(v) => `${v < 0 ? '-' : ''}$${Math.abs(v / 1000).toFixed(0)}k`} tick={{ fontSize: 8, fill: 'hsl(var(--muted-foreground))' }} />
                        <RechartsTooltip content={<CustomChartTooltip />} />
                        <ReferenceLine y={0} stroke="hsl(var(--border))" strokeWidth={1} />
                        {breakEvenMonthLabel && (
                          <ReferenceLine 
                            x={breakEvenMonthLabel} 
                            stroke="#F05B27" 
                            strokeWidth={2} 
                            strokeDasharray="4 4"
                            label={{ 
                              value: language === 'sv' ? 'Break-even' : 'Break-even', 
                              position: 'top', 
                              fontSize: 9, 
                              fill: '#F05B27',
                              fontWeight: 600
                            }} 
                          />
                        )}
                        <Area type="monotone" dataKey="withoutHostcall" stroke="#f87171" fill="url(#colorWithoutWizard)" strokeWidth={2} />
                        <Area type="monotone" dataKey="withHostcall" stroke="#22c55e" fill="url(#colorWithWizard)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CollapsibleSection>
              </motion.div>
            )}

            {/* Save & Compare Actions */}
            <motion.div variants={itemVariants}>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  size="sm"
                  onClick={() => setSaveDialogOpen(true)}
                >
                  <Save className="h-3.5 w-3.5 mr-1.5" />
                  {language === 'sv' ? 'Spara scenario' : 'Save Scenario'}
                </Button>
                {scenarios.length >= 2 && (
                  <Button 
                    variant={compareMode ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCompareMode(!compareMode)}
                  >
                    <BarChart3 className="h-3.5 w-3.5 mr-1.5" />
                    {language === 'sv' ? 'Jämför' : 'Compare'}
                  </Button>
                )}
              </div>
            </motion.div>

            {/* Saved Scenarios Panel or Comparison View */}
            {compareMode && selectedForCompare.length >= 2 ? (
              <motion.div variants={itemVariants}>
                <ScenarioComparisonView
                  scenarios={scenarios}
                  selectedIds={selectedForCompare}
                  onClose={() => setCompareMode(false)}
                  language={language}
                  formatCurrency={formatCurrency}
                />
              </motion.div>
            ) : scenarios.length > 0 && !compareMode ? (
              isCompactView ? (
                <motion.div variants={itemVariants}>
                  <CollapsibleSection
                    title={language === 'sv' ? 'Sparade scenarier' : 'Saved Scenarios'}
                    icon={Folder}
                    defaultExpanded={false}
                    badge={<Badge variant="outline" className="text-[10px] ml-2">{scenarios.length}/5</Badge>}
                  >
                    <SavedScenariosPanel
                      scenarios={scenarios}
                      selectedForCompare={selectedForCompare}
                      onToggleCompare={toggleScenarioForCompare}
                      onLoad={handleLoadScenario}
                      onDelete={deleteScenario}
                      onStartCompare={() => setCompareMode(true)}
                      language={language}
                      formatCurrency={formatCurrency}
                    />
                  </CollapsibleSection>
                </motion.div>
              ) : (
                <motion.div variants={itemVariants}>
                  <SavedScenariosPanel
                    scenarios={scenarios}
                    selectedForCompare={selectedForCompare}
                    onToggleCompare={toggleScenarioForCompare}
                    onLoad={handleLoadScenario}
                    onDelete={deleteScenario}
                    onStartCompare={() => setCompareMode(true)}
                    language={language}
                    formatCurrency={formatCurrency}
                  />
                </motion.div>
              )
            ) : null}
          </motion.div>
        );
      
      case 4: // Actions
        return (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-4"
          >
            <motion.div variants={itemVariants} className="text-center mb-3">
              <h3 className="text-xl font-bold mb-1">{language === 'sv' ? 'Börja spara idag!' : 'Start Saving Today!'}</h3>
              <p className="text-sm text-muted-foreground">{language === 'sv' ? 'Ta nästa steg för att fånga varje samtal' : 'Take the next step to capture every call'}</p>
            </motion.div>

            {/* Email Report */}
            <motion.div variants={itemVariants}>
              <Card className="p-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-brand-100 dark:bg-brand-900/30 rounded-lg">
                    <Mail className="h-5 w-5 text-brand-600 dark:text-brand-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold">{tr.emailReport}</h4>
                    <p className="text-sm text-muted-foreground">{language === 'sv' ? 'Få en detaljerad PDF-rapport' : 'Get a detailed PDF report'}</p>
                  </div>
                </div>
                
                {!emailSent ? (
                  <form onSubmit={handleEmailSubmit} className="flex gap-2 flex-wrap">
                    <Input placeholder="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="flex-1 min-w-[150px]" />
                    <Input placeholder={language === 'sv' ? 'Namn (valfritt)' : 'Name (optional)'} value={name} onChange={(e) => setName(e.target.value)} className="flex-1 min-w-[150px]" />
                    {/* Honeypot field - hidden from users, bots will fill it */}
                    <input
                      type="text"
                      name="website"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                      autoComplete="off"
                      tabIndex={-1}
                      aria-hidden="true"
                      style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0 }}
                    />
                    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                      <Button type="submit" disabled={isSubmittingEmail}>
                        {isSubmittingEmail ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" />{language === 'sv' ? 'Skicka' : 'Send'}</>}
                      </Button>
                    </motion.div>
                  </form>
                ) : (
                  <motion.div 
                    className="flex items-center gap-2 text-green-600 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <CheckCircle2 className="h-5 w-5" />
                    <span>{language === 'sv' ? 'Rapport skickad! Kolla din inbox.' : 'Report sent! Check your inbox.'}</span>
                  </motion.div>
                )}
              </Card>
            </motion.div>

            {/* Download PDF */}
            <motion.div variants={itemVariants}>
              <Card className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                      <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold">{tr.downloadPdf}</h4>
                      <p className="text-sm text-muted-foreground">{language === 'sv' ? 'Dela med ditt team' : 'Share with your team'}</p>
                    </div>
                  </div>
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button variant="outline" onClick={handleDownloadPdf}>
                      <Download className="h-4 w-4 mr-2" />
                      {language === 'sv' ? 'Ladda ner' : 'Download'}
                    </Button>
                  </motion.div>
                </div>
              </Card>
            </motion.div>

            {/* Primary CTAs */}
            <motion.div variants={itemVariants} className="grid sm:grid-cols-2 gap-4">
              <motion.div whileHover={{ scale: 1.02, y: -2 }} transition={{ type: "spring", stiffness: 400 }}>
                <Card className="p-4 bg-gradient-to-br from-brand-600 to-brand-500 text-white h-full">
                  <Rocket className="h-8 w-8 mb-3" />
                  <h4 className="font-bold text-lg mb-2">{tr.bookDemo}</h4>
                  <p className="text-sm text-white/80 mb-4">{language === 'sv' ? 'Se HostCall.ai i aktion' : 'See HostCall.ai in action'}</p>
                  <Button variant="secondary" className="w-full" asChild>
                    <Link to={`/${language}/demo`}>{tr.bookDemo}<ArrowRight className="ml-2 h-4 w-4" /></Link>
                  </Button>
                </Card>
              </motion.div>
              
              <motion.div whileHover={{ scale: 1.02, y: -2 }} transition={{ type: "spring", stiffness: 400 }}>
                <Card className="p-4 bg-gradient-to-br from-green-600 to-emerald-500 text-white h-full">
                  <Zap className="h-8 w-8 mb-3" />
                  <h4 className="font-bold text-lg mb-2">{tr.freeTrial}</h4>
                  <p className="text-sm text-white/80 mb-4">{language === 'sv' ? '14 dagar gratis, inget kort krävs' : '14 days free, no card required'}</p>
                  <Button variant="secondary" className="w-full" asChild>
                    <Link to={`/${language}/contact`}>{language === 'sv' ? 'Starta nu' : 'Start Now'}<ArrowRight className="ml-2 h-4 w-4" /></Link>
                  </Button>
                </Card>
              </motion.div>
            </motion.div>

            {/* Share */}
            <motion.div variants={itemVariants}>
              <SharePanel 
                totalImpact={totalImpactCalc.yearlyTotal} 
                paybackMonths={paybackCalc.months} 
                language={language}
                formatCurrency={formatCurrency}
              />
            </motion.div>
          </motion.div>
        );
      
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full h-[100dvh] sm:h-auto sm:max-h-[95vh] max-w-none sm:max-w-3xl lg:max-w-5xl sm:w-[95vw] overflow-hidden flex flex-col p-0 gap-0 rounded-none sm:rounded-xl">
        {/* Mode Toggle & Progress Header */}
        <div className="sticky top-0 bg-background/95 backdrop-blur-sm border-b p-4 sm:p-5 z-10 shrink-0">
          {/* Countdown Timer */}
          {countdown > 0 && currentStep < 3 && (
            <motion.div 
              className="flex items-center justify-center gap-2 mb-3"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
                countdown <= 30 
                  ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' 
                  : countdown <= 60 
                    ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                    : 'bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300'
              }`}>
                <Clock className={`h-4 w-4 ${countdown <= 30 ? 'animate-pulse' : ''}`} />
                <span>{language === 'sv' ? 'Beräkna på' : 'Calculate in'}</span>
                <motion.span 
                  key={countdown}
                  initial={{ scale: 1.2 }}
                  animate={{ scale: 1 }}
                  className="font-bold tabular-nums"
                >
                  {formatCountdown(countdown)}
                </motion.span>
              </div>
            </motion.div>
          )}
          
          {/* Completed message when countdown finished or on results */}
          {(countdown === 0 || currentStep >= 3) && currentStep < 3 && (
            <motion.div 
              className="flex items-center justify-center gap-2 mb-3"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <div className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                <CheckCircle2 className="h-4 w-4" />
                <span>{language === 'sv' ? 'Ta dig tid – vi är här för att hjälpa!' : 'Take your time – we\'re here to help!'}</span>
              </div>
            </motion.div>
          )}

          {/* Mode Toggle */}
          <div className="flex items-center justify-center gap-3 mb-4">
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button
                variant={!quickMode ? "default" : "outline"}
                size="default"
                onClick={() => { setQuickMode(false); setCurrentStep(0); }}
                className="gap-2 h-10 px-5"
              >
                <Sparkles className="h-4 w-4" />
                {tr.fullAnalysis}
              </Button>
            </motion.div>
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button
                variant={quickMode ? "default" : "outline"}
                size="default"
                onClick={() => setQuickMode(true)}
                className="gap-2 h-10 px-5"
              >
                <Zap className="h-4 w-4" />
                {tr.quickCalc}
              </Button>
            </motion.div>
          </div>

          {!quickMode && (
            <>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-bold text-base sm:text-lg">{steps[currentStep].title}</h3>
                  <p className="text-sm text-muted-foreground">{steps[currentStep].subtitle}</p>
                </div>
                <div className="flex items-center gap-2">
                  {currentStep === 3 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsCompactView(!isCompactView)}
                      className="h-8 px-3"
                    >
                      {isCompactView ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
                    </Button>
                  )}
                  <Badge variant="outline" className="shrink-0 text-xs sm:text-sm px-3 py-1">
                    {language === 'sv' ? 'Steg' : 'Step'} {currentStep + 1} {tr.stepOf} {steps.length}
                  </Badge>
                </div>
              </div>
              <StepProgress 
                currentStep={currentStep} 
                totalSteps={steps.length} 
                stepLabels={steps.map(s => s.label)}
              />
            </>
          )}
        </div>
        
        {/* Step Content */}
        <div 
          ref={scrollContainerRef}
          className="flex-1 overflow-y-auto p-5 sm:p-8 touch-pan-y"
          onScroll={handleScroll}
          {...swipeHandlers}
        >
          <AnimatePresence mode="wait">
            {quickMode ? (
              <motion.div
                key="quick"
                variants={stepVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.3, ease: "easeInOut" }}
              >
                <QuickCalculateStep
                  language={language}
                  callsPerMonth={callsPerMonth}
                  setCallsPerMonth={setCallsPerMonth}
                  numberOfProperties={numberOfProperties}
                  setNumberOfProperties={setNumberOfProperties}
                  avgOrderValue={avgOrderValue}
                  setAvgOrderValue={setAvgOrderValue}
                  quickEstimate={totalImpactCalc.yearlyTotal}
                  paybackMonths={paybackCalc.months}
                  yearOneROI={paybackCalc.yearOneROI}
                  formatCurrency={formatCurrency}
                  onSwitchToFull={() => { setQuickMode(false); setCurrentStep(0); }}
                />
              </motion.div>
            ) : (
              <motion.div
                key={currentStep}
                variants={stepVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.3, ease: "easeInOut" }}
              >
                {renderStep()}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Floating Summary Bar - visible on results step when scrolled */}
          {currentStep === 3 && (
            <FloatingSummaryBar
              annualImpact={totalImpactCalc.yearlyTotal}
              paybackMonths={paybackCalc.months}
              yearOneROI={paybackCalc.yearOneROI}
              formatCurrency={formatCurrency}
              language={language}
              visible={showFloatingBar}
              onScrollToTop={scrollToTop}
            />
          )}
        </div>
        
        {/* Navigation Footer - only show for full mode */}
        {!quickMode && (
          <div className="sticky bottom-0 bg-background/95 backdrop-blur-sm border-t p-4 sm:p-5 shrink-0">
            <div className="flex justify-between gap-4">
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => setCurrentStep(prev => Math.max(0, prev - 1))}
                  disabled={currentStep === 0}
                  className="h-12 px-6"
                >
                  <ArrowLeft className="mr-2 h-5 w-5" />
                  {tr.back}
                </Button>
              </motion.div>
              
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button
                  size="lg"
                  onClick={() => {
                    if (currentStep < steps.length - 1) {
                      setCurrentStep(prev => prev + 1);
                    } else {
                      onOpenChange(false);
                    }
                  }}
                  className="min-w-[160px] h-12 px-6"
                >
                  {currentStep === steps.length - 1 ? (
                    <>
                      <Check className="mr-2 h-5 w-5" />
                      {tr.done}
                    </>
                  ) : currentStep === 3 ? (
                    <>
                      {tr.getReport}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  ) : (
                    <>
                      {tr.next}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>
              </motion.div>
            </div>
          </div>
        )}
      </DialogContent>
      
      {/* Save Scenario Dialog */}
      <SaveScenarioDialog
        open={saveDialogOpen}
        onOpenChange={setSaveDialogOpen}
        onSave={saveScenario}
        currentInputs={getCurrentInputs()}
        currentResults={getCurrentResults()}
        language={language}
        formatCurrency={formatCurrency}
      />
    </Dialog>
  );
};

export default ROIWizard;